/**
 * Privacy Policy — the Article 13 GDPR information notice.
 *
 * It describes what this system actually does, not what a template says a
 * health app usually does: FHIR records in PostgreSQL, identity and billing in
 * MongoDB, documents in encrypted object storage, Keycloak for tokens, Stripe
 * for payments, OpenAI for the optional AI assistant, Google Fit only when the
 * traveller connects it. The retention periods repeat the ones the API reports
 * at /api/gdpr/info — if one changes, change both.
 */

import { LEGAL } from "./legalDetails";
import { LegalList, LegalPageLayout, LegalSection } from "./LegalPageLayout";

export function PrivacyPolicyPage() {
  const { operator, euRepresentative, contact, reseller } = LEGAL;

  const mail = (address: string) => (
    <a className="text-primary-600 hover:underline" href={`mailto:${address}`}>
      {address}
    </a>
  );

  return (
    <LegalPageLayout
      title="Privacy Policy"
      subtitle="What HealthPass stores about you, why, and what you can do about it."
      description="How HealthPass processes personal and health data: purposes, legal bases, processors, international transfers, retention periods and your GDPR rights."
      path="/privacy"
    >
      <p>
        HealthPass is a travel health passport. To do its job it necessarily
        handles health data, which the GDPR treats as a special category
        (Article 9). This notice says exactly what we hold, on what legal basis,
        who else sees it, and how long we keep it.
      </p>

      <LegalSection number={1} heading="Controller">
        <p>
          {operator.name}, {operator.street}, {operator.city},{" "}
          {operator.country}, is the controller for the processing described
          here, for every user wherever they live. Data protection contact:{" "}
          {mail(contact.dpo)}.
        </p>
        <p>
          We are established outside the EU but offer this service to travellers
          in it, so the GDPR applies to us under Article 3(2). Our
          representative in the Union under Article 27 is{" "}
          {euRepresentative.name}, {euRepresentative.street},{" "}
          {euRepresentative.city}, {euRepresentative.country} (
          {euRepresentative.email}). You may address it instead of us, and so
          may a supervisory authority.
        </p>
      </LegalSection>

      <LegalSection number={2} heading="What we process">
        <LegalList
          items={[
            <>
              <strong>Account data</strong> — email address, name, password
              (stored only as a hash by our identity server), language and unit
              preferences, and the devices you have signed in from.
            </>,
            <>
              <strong>Health data</strong> — conditions, allergies, medications,
              immunisations, vital signs, and documents you upload. This is what
              the product exists to hold. It is kept in a separate clinical
              database in FHIR format; uploaded documents are stored encrypted.
            </>,
            <>
              <strong>Travel data</strong> — trips, destinations, dates, and the
              destination health requirements and recommendations we derive from
              them.
            </>,
            <>
              <strong>Emergency card data</strong> — the subset of your health
              data you choose to publish on your emergency card, plus the access
              links or QR codes you generate for it.
            </>,
            <>
              <strong>Billing data</strong> — your subscription tier and its
              status, and the customer and subscription identifiers our payment
              provider assigns. We never see or store your card number.
            </>,
            <>
              <strong>Consent records</strong> — which consents you have given
              or withdrawn, and when, with an audit trail. Article 7(1) requires
              us to be able to demonstrate this.
            </>,
            <>
              <strong>Technical data</strong> — server logs including IP address
              and request metadata, error reports, and access logs recording who
              read which health record and when.
            </>,
          ]}
        />
      </LegalSection>

      <LegalSection number={3} heading="Why, and on what legal basis">
        <LegalList
          items={[
            <>
              <strong>Running your account and the paid service</strong> —
              Article 6(1)(b) (performance of a contract). Without an account
              there is nothing to store.
            </>,
            <>
              <strong>Storing and displaying your health data</strong> — your
              explicit consent, Article 9(2)(a), given at sign-up or the first
              time you add health information, and withdrawable at any time in
              Settings under &ldquo;Your consents&rdquo;. Without it we store no
              health information.
            </>,
            <>
              <strong>Sharing your emergency card</strong> — your explicit
              consent, Article 9(2)(a). Anyone holding a valid link or scanning
              your QR code can read the card without signing in; that is its
              purpose. You can revoke a link at any time, and you decide which
              fields it shows.
            </>,
            <>
              <strong>
                The AI health assistant and health recommendations
              </strong>{" "}
              — your explicit consent, Article 9(2)(a), asked for separately at
              sign-up. Using them sends the relevant part of your health and
              trip data to our AI processor (section 5). Decline, and the rest
              of the product still works.
            </>,
            <>
              <strong>Importing from a connected health service</strong> (for
              example Google Fit) — your explicit consent, granted per service
              and revocable by disconnecting it.
            </>,
            <>
              <strong>Payments, invoicing and bookkeeping</strong> — Article
              6(1)(b) and Article 6(1)(c) (legal obligation to retain accounting
              records).
            </>,
            <>
              <strong>Security, abuse prevention and audit logging</strong> —
              Article 6(1)(f) (our legitimate interest in a service that is not
              broken into), and Article 9(2)(h)/(i) in support of the security
              of health data.
            </>,
            <>
              <strong>Service emails</strong> (verification, password reset,
              billing, expiring documents) — Article 6(1)(b). Marketing email,
              if we ever send it, only on consent, Article 6(1)(a).
            </>,
            <>
              <strong>Push notifications on your device</strong> — your consent,
              Article 6(1)(a), given when your browser asks and you allow it.
              They cover your own records (an expiring document, a consent
              request) and new travel-health articles we publish. No health
              detail is put in the message. Turning notifications off in the app
              or in your browser ends all of them, including the articles, and
              costs you nothing else.
            </>,
            <>
              <strong>Partner offers</strong> — Article 6(1)(f) (our legitimate
              interest in being paid for the offers we show). We choose which
              partner offers to show you only from your country of residence and
              the destination and dates of your trip. We never use your health
              records, emergency card, vaccinations, medications or conditions
              to choose, order or hide an offer. When you follow an offer, the
              link to the partner carries only a random reference number and the
              offer&rsquo;s number &mdash; never your name, e-mail address,
              account, trip details or health data. We keep a record of that
              visit, without your account, consisting of the reference number,
              the offer, the day, your country of residence and the trip
              destination, so that we can work out what the partner owes us; it
              is deleted after 24 months. The partner may tell us that a
              contract was concluded under that reference number; it does not
              tell us who you are. You can hide all partner offers in Settings,
              and then none are chosen for you.
            </>,
          ]}
        />
      </LegalSection>

      <LegalSection number={4} heading="Nobody sees your health data unasked">
        <p>
          Inside HealthPass, your health records are readable by you and by the
          people you have granted access to: a healthcare provider you share
          with, a travel companion you nominate, or whoever holds a valid
          emergency link. Every time someone other than you opens your records
          &mdash; through an emergency link, a shared card or a provider you
          granted access &mdash; it is written to an access log you can request.
          Our staff do not browse health records; access for support or
          maintenance is restricted, logged, and only on your request or where
          it cannot be avoided in order to run the service.
        </p>
      </LegalSection>

      <LegalSection number={5} heading="Processors and third parties">
        <p>
          We use the following processors under Article 28 contracts. Each
          receives only what it needs for its part of the service.
        </p>
        <LegalList
          items={[
            <>
              <strong>Stripe</strong> — sells paid plans to you through its
              subsidiary {reseller.name} (&ldquo;{reseller.brand}&rdquo;), our
              reseller and merchant of record (Terms, section 1). For the
              purchase, payment and tax records it is a separate controller
              under its own privacy notice. It receives your email address and
              the plan you buy and handles your card details directly; we
              receive your plan, its status, and Stripe&rsquo;s customer and
              subscription identifiers.
            </>,
            <>
              <strong>OpenAI</strong> — the AI health assistant and AI-generated
              health recommendations. Receives your question and, if you have
              consented, the health context needed to answer it (for example
              your medications and allergies). Only if you use these features.
            </>,
            <>
              <strong>Google</strong> — only if you connect Google Fit, in order
              to import the fitness and vital-sign data you select.
            </>,
            <>
              <strong>Our email provider</strong> — delivery of service emails.
              Receives your email address and the message.
            </>,
            <>
              <strong>Your browser&rsquo;s push service</strong> (Google, Apple
              or Mozilla, depending on your browser) — only if you enable push
              notifications, whether about your own records or about a new
              article. It relays the notification; the message content is kept
              free of health details.
            </>,
            <>
              <strong>Our hosting provider</strong> — operates the servers on
              which all of the above data is stored.
            </>,
          ]}
        />
        <p>
          We do not sell personal data, we run no advertising trackers, and we
          do not use a third-party analytics service. How we count blog readers
          instead is described in section&nbsp;11.
        </p>
      </LegalSection>

      <LegalSection number={6} heading="Transfers outside the EU/EEA">
        <p>
          Your account, health, travel and billing data is stored on servers
          operated for us by Hetzner Online GmbH in Ashburn, Virginia, in the
          United States. Two further processors may process data there as well:
          OpenAI, when you use the AI features, and Google, when you connect
          Google Fit. Paid plans are sold by {reseller.name}, a Stripe company
          in the United States.
        </p>
        <p>
          These transfers rest on the European Commission&rsquo;s Standard
          Contractual Clauses, supplemented by encryption in transit and of
          uploaded documents and backups. US law does not offer a level of
          protection equivalent to the GDPR in every respect &mdash; in
          particular, US authorities may have access rights that EU law would
          not grant. We tell you this plainly because your health data is
          special-category data under Article 9 and you are entitled to decide
          with the facts in front of you.
        </p>
      </LegalSection>

      <LegalSection number={7} heading="How long we keep it">
        <LegalList
          items={[
            <>
              <strong>Health records</strong> — while your account exists, and
              then as required by applicable medical documentation law (up to
              seven years after the last activity) unless you ask for erasure
              and no such obligation stands in the way.
            </>,
            <>
              <strong>Account data</strong> — until you delete your account.
            </>,
            <>
              <strong>Audit and access logs</strong> — seven years, as evidence
              of who accessed health data.
            </>,
            <>
              <strong>Consent records</strong> — kept as long as we must be able
              to demonstrate the consent, including after withdrawal.
            </>,
            <>
              <strong>Billing records</strong> — ten years, under tax and
              commercial law.
            </>,
            <>
              <strong>Server logs</strong> — a short operational period, then
              deleted.
            </>,
          ]}
        />
      </LegalSection>

      <LegalSection number={8} heading="Deleting your account">
        <p>
          You can request deletion from your settings. The request is carried
          out within 30 days: your health records, documents, trips and
          emergency card are erased, and your identity account is removed. What
          remains is only what the law requires us to keep &mdash; billing
          records and the audit trail &mdash; and that is locked to
          storage-restricted processing, not used for anything else.
        </p>
      </LegalSection>

      <LegalSection number={9} heading="Your rights">
        <LegalList
          items={[
            <>
              <strong>Access</strong> (Article 15) and{" "}
              <strong>portability</strong> (Article 20) — export everything we
              hold in a machine-readable format from your settings, at any time.
            </>,
            <>
              <strong>Rectification</strong> (Article 16) — edit your data in
              the app.
            </>,
            <>
              <strong>Erasure</strong> (Article 17) — see section 8.
            </>,
            <>
              <strong>Restriction</strong> (Article 18) and{" "}
              <strong>objection</strong> (Article 21) — write to{" "}
              {mail(contact.privacy)}.
            </>,
            <>
              <strong>Withdrawal of consent</strong> (Article 7(3)) — at any
              time, in Consent settings. Withdrawal does not affect processing
              already carried out.
            </>,
            <>
              <strong>Complaint</strong> (Article 77) — to the supervisory
              authority of the EU member state where you live, work, or where
              you believe the breach happened. Our EU representative (section 1)
              is there to be addressed on this too.
            </>,
          ]}
        />
      </LegalSection>

      <LegalSection number={10} heading="Security">
        <p>
          Data is encrypted in transit (TLS), uploaded documents and backups are
          stored encrypted, the databases run on access-restricted servers,
          authentication runs through a dedicated identity server with
          short-lived access tokens, and access to health data is checked on the
          server for every request; access by anyone other than you is written
          to an audit log. Backups are encrypted. No system is beyond reach: if
          a breach ever puts your rights at risk, we notify you and the
          supervisory authority as Articles 33 and 34 require.
        </p>
      </LegalSection>

      <LegalSection number={11} heading="Storage on your device">
        <p>
          HealthPass is an offline-capable web app. It stores your session token
          and a copy of your data in your browser&rsquo;s local storage so the
          app works without a connection and can sync your changes later. This
          is technically necessary for the service you asked for; we set no
          tracking or advertising cookies. Signing out clears it.
        </p>
        <p>
          <strong>Reading an article on our blog.</strong> We count how often
          each article is read, how far down the page readers get, and how often
          the link at the end is followed. Nothing is stored on your device for
          this, and no third party is involved. To tell two readers apart for
          one day, our server derives a short code from your IP address and
          browser identification using a secret key that is replaced every night
          and never kept. Once that key is gone the codes cannot be traced back
          to anyone, including by us; your IP address is never stored. The
          detailed records are deleted after 30 days, leaving only per-article
          daily totals, which are not personal data.
        </p>
        <p>
          <strong>Visiting our home, pricing and sign-up pages.</strong> We
          count visits to these pages, and what kind of source each came from (a
          search engine, a social network, a newsletter, a partner or another
          website), by the same method as the blog: nothing is stored on your
          device, no third party is involved, your IP address is never stored,
          and the detailed records are deleted after 30 days, leaving daily
          totals. If you then create an account, we keep with it how you arrived
          &mdash; the campaign tags in the link you followed, the name of the
          referring website (never the page), and a partner&rsquo;s referral
          code &mdash; and note when the account first starts a checkout and
          first pays, so that we can tell which channels and partners bring
          customers and pay partners their commission. We never record which
          blog article you read before signing up. This is kept for as long as
          the account exists. The legal basis is our legitimate interest in
          measuring and paying for our marketing (Article 6(1)(f)). If your
          browser sends Global Privacy Control or Do Not Track, we count none of
          these visits and keep no record of how you arrived.
        </p>
      </LegalSection>

      <LegalSection number={12} heading="Children">
        <p>
          HealthPass is for users aged 16 and over. A parent or guardian may
          keep a record on behalf of a child, and is then responsible for the
          consent that record rests on.
        </p>
      </LegalSection>

      <LegalSection number={13} heading="Changes">
        <p>
          If we change this notice materially we tell you in the app or by email
          before the change takes effect. Where the change concerns processing
          that rests on your consent, we ask for it again.
        </p>
      </LegalSection>
    </LegalPageLayout>
  );
}

export default PrivacyPolicyPage;
